import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { FaUsers, FaSearch, FaFilter, FaEdit, FaBan, FaCheck, FaEye, FaChevronLeft, FaChevronRight, FaTrash } from 'react-icons/fa';
import { adminAPI } from '../services/api';
import { toast } from 'react-toastify';

const UserManagement = () => {
  const { user } = useSelector((state) => state.auth);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    is_generated: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(''); // 'suspend' or 'ban'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    reason: '',
    until: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      const response = await adminAPI.getUsers(params);
      setUsers(response.data.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleSuspendUser = (userData) => {
    setSelectedUser(userData);
    setModalAction('suspend');
    setFormData({ reason: '', until: '' });
    setShowModal(true);
  };

  const handleActivateUser = async (userId, username) => {
    toast.info(`Activation de ${username} en cours...`);

    try {
      await adminAPI.activateUser(userId);
      toast.success('Utilisateur activé avec succès');
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error activating user:', error);
      toast.error('Erreur lors de l\'activation');
    }
  };

  const handleEditUser = (userData) => {
    // For now, show a toast. In a real app, you'd open a modal
    toast.info(`Edition de ${userData.username} - Fonctionnalité à implémenter`);
  };

  const handleBanUser = (userData) => {
    setSelectedUser(userData);
    setModalAction('ban');
    setFormData({ reason: '', until: '' });
    setShowModal(true);
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement l'utilisateur ${username} ? Cette action est irréversible.`)) {
      return;
    }

    toast.info(`Suppression de ${username} en cours...`);

    try {
      await adminAPI.deleteUser(userId);
      toast.success('Utilisateur supprimé avec succès');
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleModalSubmit = async () => {
    if (!formData.reason.trim()) {
      toast.error('Veuillez saisir un motif');
      return;
    }

    if (modalAction === 'suspend' && !formData.until) {
      toast.error('Veuillez saisir une date de fin pour la suspension');
      return;
    }

    toast.info(`${modalAction === 'suspend' ? 'Suspension' : 'Bannissement'} de ${selectedUser.username} en cours...`);

    try {
      const data = { reason: formData.reason };
      if (modalAction === 'suspend') {
        data.until = formData.until;
        await adminAPI.suspendUser(selectedUser.id, data);
        toast.success('Utilisateur suspendu avec succès');
      } else {
        await adminAPI.banUser(selectedUser.id, data);
        toast.success('Utilisateur banni avec succès');
      }
      setShowModal(false);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error(`Error ${modalAction}ing user:`, error);
      toast.error(`Erreur lors du ${modalAction === 'suspend' ? 'bannissement' : 'bannissement'}`);
    }
  };

  const getStatusBadge = (userData) => {
    if (userData.is_suspended) {
      return <span className="badge bg-danger">Suspendu</span>;
    }
    if (!userData.is_active) {
      return <span className="badge bg-secondary">Inactif</span>;
    }
    return <span className="badge bg-success">Actif</span>;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'superadmin':
        return <span className="badge bg-danger">Super Admin</span>;
      case 'admin':
        return <span className="badge bg-warning">Admin</span>;
      case 'lover':
        return <span className="badge bg-primary">Utilisateur</span>;
      default:
        return <span className="badge bg-secondary">Non défini</span>;
    }
  };

  const getGeneratedBadge = (isGenerated) => {
    if (isGenerated === true || isGenerated === 1 || isGenerated === '1') {
      return <span className="badge bg-info ms-1" style={{fontSize: '0.7rem'}}>🤖 Généré</span>;
    }
    return null;
  };



  // Check if user is admin or superadmin
  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="user-management min-vh-100" style={{ backgroundColor: 'var(--lova-bg-secondary)' }}>
      <div className="container-fluid py-4">
        {/* Header */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="h2 mb-1">Gestion des Utilisateurs</h1>
                <p className="text-muted mb-0">Gérer les comptes utilisateurs et leurs permissions</p>
              </div>
              <div className="text-end">
                <small className="text-muted">Page {currentPage} sur {totalPages}</small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="input-group">
                      <span className="input-group-text"><FaSearch /></span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Rechercher par nom, email, username..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filters.role}
                      onChange={(e) => handleFilterChange('role', e.target.value)}
                    >
                      <option value="">Tous les rôles</option>
                      <option value="lover">Utilisateur</option>
                      <option value="admin">Admin</option>
                      {user.role === 'superadmin' && <option value="superadmin">Super Admin</option>}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="">Tous les statuts</option>
                      <option value="active">Actif</option>
                      <option value="suspended">Suspendu</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={filters.is_generated}
                      onChange={(e) => handleFilterChange('is_generated', e.target.value)}
                    >
                      <option value="">Tous les utilisateurs</option>
                      <option value="true">🤖 Utilisateurs générés</option>
                      <option value="false">👤 Utilisateurs réels</option>
                    </select>
                  </div>
                  <div className="col-md-2">

                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <FaFilter /> Filtres
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th>Utilisateur</th>
                          <th>Email</th>
                          <th>Rôle</th>
                          <th>Statut</th>
                          <th>Inscription</th>
                          <th>Dernière activité</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((userData) => (
                          <tr key={userData.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="avatar-circle me-3">
                                  {userData.first_name?.[0] || userData.username?.[0] || '?'}
                                </div>
                                <div>
                                  <div className="fw-bold">{userData.first_name} {userData.last_name}</div>
                                  <small className="text-muted">@{userData.username}</small>
                                  {getGeneratedBadge(userData.is_generated)}
                                </div>
                              </div>
                            </td>

                            <td>{userData.email}</td>
                            <td>{getRoleBadge(userData.role)}</td>
                            <td>{getStatusBadge(userData)}</td>
                            <td>{new Date(userData.date_joined).toLocaleDateString('fr-FR')}</td>
                            <td>
                              {userData.last_active
                                ? new Date(userData.last_active).toLocaleDateString('fr-FR')
                                : 'Jamais'
                              }
                            </td>
                            <td>
                              <div className="btn-group btn-group-sm">
                                <button
                                  className="btn btn-outline-primary"
                                  onClick={() => handleEditUser(userData)}
                                  title="Modifier"
                                  disabled={userData.id === user.id}
                                >
                                  <FaEdit />
                                </button>
                                {userData.is_suspended ? (
                                  <button
                                    className="btn btn-outline-success"
                                    onClick={() => handleActivateUser(userData.id, userData.username)}
                                    title="Activer"
                                    disabled={userData.id === user.id}
                                  >
                                    <FaCheck />
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      className="btn btn-outline-warning"
                                      onClick={() => handleSuspendUser(userData)}
                                      title="Suspendre"
                                      disabled={userData.id === user.id || (userData.role === 'superadmin' && user.role !== 'superadmin')}
                                    >
                                      <FaBan />
                                    </button>
                                    <button
                                      className="btn btn-outline-danger"
                                      onClick={() => handleBanUser(userData)}
                                      title="Bannir"
                                      disabled={userData.id === user.id || (userData.role === 'superadmin' && user.role !== 'superadmin')}
                                    >
                                      <FaBan />
                                    </button>
                                  </>
                                )}
                                {user.role === 'superadmin' && (
                                  <button
                                    className="btn btn-outline-dark"
                                    onClick={() => handleDeleteUser(userData.id, userData.username)}
                                    title="Supprimer"
                                    disabled={userData.id === user.id || userData.role === 'superadmin'}
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="card-footer bg-white border-0">
                  <nav>
                    <ul className="pagination justify-content-center mb-0">
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        >
                          <FaChevronLeft />
                        </button>
                      </li>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                        return (
                          <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(page)}
                            >
                              {page}
                            </button>
                          </li>
                        );
                      })}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        >
                          <FaChevronRight />
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalAction === 'suspend' ? 'Suspendre' : 'Bannir'} l'utilisateur {selectedUser?.username}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="reason" className="form-label">Motif *</label>
                  <textarea
                    className="form-control"
                    id="reason"
                    rows="3"
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Saisissez le motif de la suspension/bannissement..."
                  ></textarea>
                </div>
                {modalAction === 'suspend' && (
                  <div className="mb-3">
                    <label htmlFor="until" className="form-label">Date de fin de suspension *</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      id="until"
                      value={formData.until}
                      onChange={(e) => setFormData(prev => ({ ...prev, until: e.target.value }))}
                      min={new Date().toISOString().slice(0, 16)}
                    />
                    <small className="text-muted">L'utilisateur sera automatiquement réactivé à cette date.</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Annuler
                </button>
                <button type="button" className="btn btn-primary" onClick={handleModalSubmit}>
                  {modalAction === 'suspend' ? 'Suspendre' : 'Bannir'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .avatar-circle {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: var(--lova-blue);
          color: var(--lova-text-light);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        .min-vh-100 {
          min-height: 100vh;
        }
        .table th {
          border-top: none !important;
          font-weight: 600 !important;
          font-size: 0.875rem !important;
          text-transform: uppercase !important;
          letter-spacing: 0.5px !important;
          background: var(--lova-bg-secondary) !important;
          color: var(--lova-text-primary) !important;
          border-bottom: 2px solid var(--lova-gray-light) !important;
        }
        .table td {
          background: var(--lova-bg-primary) !important;
          color: var(--lova-text-primary) !important;
        }
        .table tbody tr {
          background: var(--lova-bg-primary) !important;
        }
        .table tbody tr:hover {
          background: var(--lova-bg-secondary) !important;
        }
        .btn-group-sm .btn {
          padding: 0.25rem 0.5rem;
        }
      `}</style>
    </div>
  );
};

export default UserManagement;
